import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import scrollLockManager from '../utils/scrollLockManager';

/**
 * useScrollLock - React hook for managing scroll lock behavior in modals
 * Optimized for performance with memoization and efficient state management
 * 
 * @param {Object} options - Configuration options
 * @param {boolean} options.isActive - Whether scroll lock should be active
 * @param {boolean} options.restoreScrollOnUnmount - Whether to restore scroll on component unmount
 * @returns {Object} Hook return object with isLocked state
 */
const useScrollLock = (options = {}) => {
  const {
    isActive = false,
    restoreScrollOnUnmount = true
  } = options;

  const [isLocked, setIsLocked] = useState(false);
  const isActiveRef = useRef(isActive);
  const hasLockedRef = useRef(false);
  const performanceMetrics = useRef({
    lockTime: 0,
    unlockTime: 0,
    lockCount: 0
  });

  // Update ref when isActive changes
  useEffect(() => {
    isActiveRef.current = isActive;
  }, [isActive]);

  // Memoized lock function for performance
  const lockScroll = useCallback(() => {
    if (hasLockedRef.current) return;

    const startTime = performance.now ? performance.now() : Date.now();
    
    try {
      scrollLockManager.lock();
      hasLockedRef.current = true;
      setIsLocked(true);
      
      // Track performance metrics
      const endTime = performance.now ? performance.now() : Date.now();
      performanceMetrics.current.lockTime = endTime - startTime;
      performanceMetrics.current.lockCount++;
      
      // Log performance warning if lock takes too long
      if (performanceMetrics.current.lockTime > 16) { // More than one frame at 60fps
        console.warn(`useScrollLock: Lock operation took ${performanceMetrics.current.lockTime.toFixed(2)}ms`);
      }
    } catch (error) {
      console.warn('useScrollLock: Failed to lock scroll', error);
    }
  }, []);

  // Memoized unlock function for performance
  const unlockScroll = useCallback(() => {
    if (!hasLockedRef.current) return;

    const startTime = performance.now ? performance.now() : Date.now();
    
    try {
      scrollLockManager.unlock();
      hasLockedRef.current = false;
      setIsLocked(false);
      
      // Track performance metrics
      const endTime = performance.now ? performance.now() : Date.now();
      performanceMetrics.current.unlockTime = endTime - startTime;
      
      // Log performance warning if unlock takes too long
      if (performanceMetrics.current.unlockTime > 16) { // More than one frame at 60fps
        console.warn(`useScrollLock: Unlock operation took ${performanceMetrics.current.unlockTime.toFixed(2)}ms`);
      }
    } catch (error) {
      console.warn('useScrollLock: Failed to unlock scroll', error);
    }
  }, []);

  // Handle scroll lock/unlock based on isActive with optimized effect
  useEffect(() => {
    if (isActive && !hasLockedRef.current) {
      lockScroll();
    } else if (!isActive && hasLockedRef.current) {
      unlockScroll();
    }
  }, [isActive, lockScroll, unlockScroll]);

  // Optimized cleanup on unmount
  useEffect(() => {
    return () => {
      if (hasLockedRef.current && restoreScrollOnUnmount) {
        unlockScroll();
      }
    };
  }, [restoreScrollOnUnmount, unlockScroll]);

  // Emergency cleanup on page unload
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (hasLockedRef.current) {
        scrollLockManager.cleanup();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  // Memoized return value to prevent unnecessary re-renders
  const returnValue = useMemo(() => ({
    isLocked: isLocked || scrollLockManager.isLocked(),
    // Expose performance metrics in development
    ...(process.env.NODE_ENV === 'development' && {
      _performanceMetrics: performanceMetrics.current
    })
  }), [isLocked]);

  return returnValue;
};

export default useScrollLock;